import { filter } from 'ramda';
import { formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';
import differenceInDays from 'date-fns/differenceInDays';
import isWithinInterval from 'date-fns/isWithinInterval';

import { filterByString } from '@topconagriculture/frontend-utils';

import { Forms } from '../../form/form.selector';
import { RootState } from '../../reducers';
import { Group } from './group.types';
import { createFeedingListFormValues } from '../../../core/helpers/utils/utils';
import { getDaysBack } from '../../../shared/DateRangePicker/utils';
import { getSelectedBunkReaderPen } from '../../selected/selected.selector';
import { toFarmTimeZone } from '../../../core/helpers/utils/dateUtils';
import { getTimeZone } from '../settings/settings.selector';

export const getAllGroups = (state: RootState): Group[] => state.entities.group;

export const getGroupList = createSelector([getAllGroups], (groups) => groups.filter((group) => !group.deleted));

const groupsFilterForm = formValueSelector(Forms.groupsFilterForm);
const feedingListSearchForm = formValueSelector(Forms.feedingListSearch);
const feeadingListSearchField = (state: RootState) => feedingListSearchForm(state, 'search');
const groupTypeSelector = (state: RootState) => groupsFilterForm(state, 'groupType');
const groupNameSelector = (state: RootState) => groupsFilterForm(state, 'groupName');
const groupStateSelector = (state: RootState) => groupsFilterForm(state, 'groupState');

export const getGroupListSorted = createSelector([getGroupList], (groupList): Group[] =>
  groupList.sort((a, b) => a.description.localeCompare(b.description, undefined, { numeric: true })),
);

export const getGroupListFilteredBySearch = createSelector(
  [getGroupList, feeadingListSearchField],
  (groupList, feedingListSearchField): Group[] => {
    const filterGroups = feedingListSearchField
      ? filter(
          (group) =>
            filterByString(group.displayName, feedingListSearchField) ||
            filterByString(group.description, feedingListSearchField) ||
            filterByString(group.rationNames && group.rationNames.join(', '), feedingListSearchField),
          groupList,
        )
      : groupList;

    return filterGroups.filter((group) => !group.premix && !group.notInUse);
  },
);

export const getGroupsNoPremixes = createSelector([getGroupList], (groups): Group[] =>
  groups.filter((group) => !group.premix),
);

export const getAllGroupsWithNoPremixes = createSelector([getAllGroups], (groups) =>
  groups.filter((group) => !group.premix),
);

export const getGroupListFiltered = createSelector(
  [getGroupListSorted, groupTypeSelector, groupNameSelector, groupStateSelector],
  (groupList: Group[], groupTypeField, groupNameField, groupStateField: boolean): Group[] => {
    let filtered = groupList.filter((group) => !group.premix);

    if (!!groupNameField) {
      filtered = filtered.filter(
        (group) =>
          group.displayName.toLowerCase().includes(groupNameField.toLocaleLowerCase()) ||
          group.description.toLowerCase().includes(groupNameField.toLocaleLowerCase()),
      );
    }

    if (groupStateField) {
      filtered = filtered.filter((group) => !group.notInUse);
    }

    if (groupTypeField !== -1) {
      filtered = filtered.filter((group) => group.groupTypeId === groupTypeField);
    }

    return filtered;
  },
);

export const getGroupById = createSelector(
  getAllGroups,
  (groupList: Group[]) => (id: string) => groupList.find((group) => group.id === id)!,
);

export const hasFilters = createSelector(
  [groupTypeSelector, groupNameSelector],
  (groupType, groupName): boolean => groupType !== -1 || !!groupName,
);

export const getPremixFeedings = (state: RootState, groupId: string, rationId: string) => {
  const group = getGroupById(state)(groupId);
  return ((group && group.feedings) || []).filter((x) => x.rationId === rationId);
};

export const getGroupListInUse = createSelector([getGroupsNoPremixes], (groups: Group[]) =>
  groups.filter((x) => !x.notInUse),
);

export const getPensWithRecipe = createSelector([getGroupList], (groups: Group[]) => (recipeId: string): Group[] => {
  return groups.filter((group) => group.rationIds?.includes(recipeId));
});

export const getGroupsNoPremixesInUse = createSelector([getGroupsNoPremixes], (groups: Group[]): Group[] =>
  groups.filter((group) => !group.notInUse),
);

export const getFeedingListFormInitialValues = createSelector([getGroupsNoPremixesInUse], (initialValues) => {
  return initialValues.reduce(createFeedingListFormValues, {});
});

export const getBunkReaderDaysBack = createSelector(
  [(state) => formValueSelector(Forms.bunkReaderDateRange)(state, 'selectedTimeRange')],
  (selectedTimeRange) => {
    const daysBack =
      getDaysBack(
        selectedTimeRange?.labelKey,
        differenceInDays(selectedTimeRange?.endDate, selectedTimeRange?.startDate),
      ) || 0;
    return daysBack + 1;
  },
);

export const getSelectedBunkReaderNotes = createSelector(
  [
    (state) => getSelectedBunkReaderPen(state),
    getTimeZone,
    (state) => formValueSelector(Forms.bunkReaderDateRange)(state, 'selectedTimeRange'),
  ],
  (selectedBunkReader, timezone, selectedTimeRange) => {
    return selectedBunkReader?.notes?.filter((note) =>
      isWithinInterval(toFarmTimeZone(new Date(note?.timestamp), timezone), {
        start: selectedTimeRange.startDate,
        end: selectedTimeRange.endDate,
      }),
    );
  },
);
